import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Button,
  Box,
  Pagination,
  Container,
  IconButton
} from '@mui/material';
import type { Theme } from '@mui/material';
import type { SxProps } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getBlogPosts, getBlogPostsByCategory } from '../utils/blogUtils';
import type { BlogPost } from '../types/blog';

export default function BlogList() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const postsPerPage = 6;

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const allPosts = category 
          ? await getBlogPostsByCategory(category)
          : await getBlogPosts();
        
        const startIndex = (page - 1) * postsPerPage;
        const endIndex = startIndex + postsPerPage;
        const paginatedPosts = allPosts.slice(startIndex, endIndex);
        
        setPosts(paginatedPosts);
        setTotalPages(Math.ceil(allPosts.length / postsPerPage));
      } catch (error) {
        console.error('Error loading posts:', error);
        setPosts([]);
      }
    };
    loadPosts();
  }, [category, page]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const handlePostClick = (slug: string) => {
    navigate(`/post/${slug}`);
  };

  const handleBackClick = () => {
    navigate('/');
  };

  const getCategoryImage = (category: string) => {
    const images = {
      travel: 'https://source.unsplash.com/random/800x600/?travel',
      ai: 'https://source.unsplash.com/random/800x600/?artificial-intelligence',
      general: 'https://source.unsplash.com/random/800x600/?blog'
    };
    return images[category as keyof typeof images] || images.general;
  };

  const cardStyle: SxProps<Theme> = {
    height: '100%', 
    display: 'flex', 
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.02)',
      cursor: 'pointer'
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton 
            onClick={handleBackClick}
            sx={{ mr: 2 }}
            aria-label="back to categories"
          >
            <ArrowBackIcon />
          </IconButton>
          {category && (
            <Typography variant="h4" component="h1">
              {category.charAt(0).toUpperCase() + category.slice(1)} Posts
            </Typography>
          )}
        </Box>
        
        <Grid container spacing={4}>
          {posts.map((post) => (
            <Grid 
              item
              key={post.slug}
              xs={12}
              sm={6}
              md={4}
              sx={{ p: 2 }}
            >
              <Card 
                sx={cardStyle}
                onClick={() => handlePostClick(post.slug)}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={getCategoryImage(post.category)}
                  alt={post.title}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography 
                    gutterBottom 
                    variant="h6" 
                    component="h2" 
                    sx={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {post.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {post.content.split('\n\n')[1] || ''} {/* Show first paragraph as excerpt */}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button 
                      size="small" 
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/category/${post.category}`);
                      }}
                    >
                      {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(post.date).toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}
      </Box>
    </Container>
  );
}